/**
 * Hero rotativo (estilo Magalu/Havan) — `tenant.theme_json.storeHero`.
 * Independente da vitrine modular; slides partilhados via resolveHeroSlides.
 */

import { normalizeThemeJson, isVitrineItemEnabled, parseStoreHomeLayout } from './storeHomeLayout.js';

export const STORE_HERO_MAX_SLIDES = 6;
export const STORE_HERO_AUTOPLAY_MIN = 3;
export const STORE_HERO_AUTOPLAY_MAX = 10;
export const STORE_HERO_AUTOPLAY_DEFAULT = 5;
/** Hint no painel — proporção ~3:1. */
export const STORE_HERO_SIZE_HINT = '1200×400 px (proporção 3:1)';

/** @typedef {'none'|'secao'|'product'|'url'} StoreHeroLinkType */

/**
 * @param {unknown} n
 * @returns {number}
 */
export function clampHeroAutoplaySeconds(n) {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x)) return STORE_HERO_AUTOPLAY_DEFAULT;
  return Math.min(STORE_HERO_AUTOPLAY_MAX, Math.max(STORE_HERO_AUTOPLAY_MIN, x));
}

/**
 * @param {unknown} raw
 * @returns {StoreHeroLinkType}
 */
export function normalizeHeroLinkType(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'secao' || s === 'section' || s === 'category') return 'secao';
  if (s === 'product' || s === 'produto') return 'product';
  if (s === 'url' || s === 'link' || s === 'external') return 'url';
  return 'none';
}

/**
 * Normaliza um slide (novo ou legado da vitrine).
 * @param {unknown} raw
 * @returns {null|{
 *   enabled: boolean,
 *   image_url: string,
 *   link_type: StoreHeroLinkType,
 *   secao: string,
 *   product_id: number|null,
 *   href: string,
 * }}
 */
export function normalizeHeroSlide(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  /** @type {Record<string, unknown>} */
  const o = raw;
  const image_url = typeof o.image_url === 'string' ? o.image_url.trim() : '';
  let link_type = normalizeHeroLinkType(o.link_type);
  const href = typeof o.href === 'string' ? o.href.trim() : '';
  const secao = o.secao != null ? String(o.secao).trim() : '';
  let product_id = null;
  const pid = Number(o.product_id);
  if (Number.isFinite(pid) && pid >= 1) product_id = Math.trunc(pid);

  // Legado: só href sem link_type → url
  if (!Object.prototype.hasOwnProperty.call(o, 'link_type') && href) {
    link_type = 'url';
  }

  return {
    enabled: isVitrineItemEnabled(o),
    image_url,
    link_type,
    secao,
    product_id,
    href,
  };
}

/**
 * @param {unknown} themeJson
 * @returns {{
 *   enabled: boolean,
 *   autoplaySeconds: number,
 *   slides: NonNullable<ReturnType<typeof normalizeHeroSlide>>[],
 * }}
 */
export function parseStoreHero(themeJson) {
  const base = normalizeThemeJson(themeJson);
  const raw = base.storeHero;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      enabled: false,
      autoplaySeconds: STORE_HERO_AUTOPLAY_DEFAULT,
      slides: [],
    };
  }
  const slidesRaw = Array.isArray(raw.slides) ? raw.slides : [];
  const slides = [];
  for (const s of slidesRaw) {
    if (slides.length >= STORE_HERO_MAX_SLIDES) break;
    const n = normalizeHeroSlide(s);
    if (n) slides.push(n);
  }
  return {
    enabled: Boolean(raw.enabled),
    autoplaySeconds: clampHeroAutoplaySeconds(raw.autoplaySeconds ?? raw.autoplay_seconds),
    slides,
  };
}

/**
 * Slides activos para a loja: storeHero.slides, senão hero legado da vitrine.
 * @param {unknown} themeJson
 */
export function resolveHeroSlides(themeJson) {
  const hero = parseStoreHero(themeJson);
  let list = hero.slides.filter((s) => s.enabled && s.image_url !== '');
  if (list.length === 0) {
    const home = parseStoreHomeLayout(themeJson);
    list = [];
    for (const s of home.hero) {
      if (list.length >= STORE_HERO_MAX_SLIDES) break;
      const n = normalizeHeroSlide(s);
      if (n && n.enabled && n.image_url !== '') list.push(n);
    }
  }
  return list;
}

/**
 * Deve mostrar o carrossel (fora de luxo/lista/filtros — o caller aplica esses gates).
 * @param {unknown} themeJson
 */
export function shouldShowStoreHero(themeJson) {
  const hero = parseStoreHero(themeJson);
  const home = parseStoreHomeLayout(themeJson);
  if (!hero.enabled && !home.enabled) return false;
  return resolveHeroSlides(themeJson).length > 0;
}

/**
 * Corpo para gravar no theme (sem campos lixo).
 * @param {{
 *   enabled?: boolean,
 *   autoplaySeconds?: number,
 *   slides?: unknown[],
 * }} cfg
 */
export function storeHeroForSave(cfg) {
  const slidesIn = Array.isArray(cfg?.slides) ? cfg.slides : [];
  const slides = [];
  for (const s of slidesIn) {
    if (slides.length >= STORE_HERO_MAX_SLIDES) break;
    const n = normalizeHeroSlide(s);
    if (!n) continue;
    slides.push({
      enabled: n.enabled,
      image_url: n.image_url,
      link_type: n.link_type,
      secao: n.secao,
      product_id: n.product_id,
      href: n.href,
    });
  }
  return {
    enabled: Boolean(cfg?.enabled),
    autoplaySeconds: clampHeroAutoplaySeconds(cfg?.autoplaySeconds),
    slides,
  };
}
